<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import WhatsAppCampaignReportDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignReportDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const showWhatsAppCreateDialog = ref(false);
const showWhatsAppEditDialog = ref(false);
const showReportDialog = ref(false);
const activeCampaignForModal = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

let pollTimer = null;

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get', { campaignType: 'one_off' });

  // Polling automático da lista de campanhas a cada 5 segundos se houver alguma em execução
  pollTimer = setInterval(() => {
    const list = WhatsAppCampaigns.value || [];
    const hasRunning = list.some(c => c.campaign_status === 'processing' || c.campaign_status === 'active');
    if (hasRunning) {
      store.dispatch('campaigns/get', { campaignType: 'one_off' });
    }
  }, 5000);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const handleView = campaign => {
  activeCampaignForModal.value = campaign;
  showReportDialog.value = true;
};

const handleEdit = campaign => {
  activeCampaignForModal.value = campaign;
  showWhatsAppEditDialog.value = true;
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handlePause = async campaign => {
  try {
    await store.dispatch('campaigns/update', {
      id: campaign.id,
      campaign_status: 'paused',
    });
    useAlert('Campanha pausada com sucesso!');
    await store.dispatch('campaigns/get', { campaignType: 'one_off' });
  } catch (err) {
    useAlert('Erro ao pausar campanha.');
  }
};

const handleResume = async campaign => {
  try {
    await store.dispatch('campaigns/update', {
      id: campaign.id,
      campaign_status: 'active',
    });
    useAlert('Campanha retomada com sucesso!');
    await store.dispatch('campaigns/get', { campaignType: 'one_off' });
  } catch (err) {
    useAlert('Erro ao retomar campanha.');
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE') || 'Campanhas do WhatsApp'"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN') || 'Criar Campanha'"
    @click="showWhatsAppCreateDialog = true"
    @close="showWhatsAppCreateDialog = false"
  >
    <template #action>
      <!-- Modal Criar Campanha -->
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCreateDialog"
        mode="create"
        @close="showWhatsAppCreateDialog = false"
      />

      <!-- Modal Editar Campanha -->
      <WhatsAppCampaignDialog
        v-if="showWhatsAppEditDialog"
        :campaign="activeCampaignForModal"
        mode="edit"
        @close="showWhatsAppEditDialog = false"
      />

      <!-- Modal Mini Dashboard / Relatório em Tempo Real -->
      <WhatsAppCampaignReportDialog
        v-if="showReportDialog"
        :campaign="activeCampaignForModal"
        @close="showReportDialog = false"
      />
    </template>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      @view="handleView"
      @edit="handleEdit"
      @delete="handleDelete"
      @pause="handlePause"
      @resume="handleResume"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>